/**
 * Invoice Generator Utility for Kisan Daily Fresh
 * Matches the official Kisan Daily Fresh Sales Invoice format.
 */
import { INVOICE_LOGO_BASE64 } from "./invoiceLogo.js";
import { apiFetch } from "./apiFetch";
import toast from "react-hot-toast";

/**
 * Format number to 2 decimal places
 */
const formatNumber = (val) => {
  const n = parseFloat(val ?? 0);
  return isNaN(n) ? "0.00" : n.toFixed(2);
};

/**
 * Format date in '19 Aug 2026' style
 */
const formatInvoiceDate = (iso) => {
  if (!iso) {
    const d = new Date();
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(iso);
  }
};

/**
 * Convert number to Indian currency words
 * e.g., 527 -> "Five Hundred Twenty Seven Rupees and Zero Paisa Only"
 */
export const amountToWordsIndian = (amount) => {
  const num = Math.round(Number(amount || 0) * 100) / 100;
  if (isNaN(num) || num < 0) return "Zero Rupees and Zero Paisa Only";

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  function convertTwoDigits(n) {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    const ten = Math.floor(n / 10);
    const unit = n % 10;
    return tens[ten] + (unit > 0 ? "-" + ones[unit] : "");
  }

  function convertThreeDigits(n) {
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    let res = "";
    if (hundred > 0) {
      res += ones[hundred] + " Hundred";
      if (rest > 0) res += " ";
    }
    if (rest > 0) {
      res += convertTwoDigits(rest);
    }
    return res;
  }

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  if (integerPart === 0 && decimalPart === 0) {
    return "Zero Rupees and Zero Paisa Only";
  }

  let parts = [];
  const crore = Math.floor(integerPart / 10000000);
  let remainder = integerPart % 10000000;
  const lakh = Math.floor(remainder / 100000);
  remainder = remainder % 100000;
  const thousand = Math.floor(remainder / 1000);
  const hundred = remainder % 1000;

  if (crore > 0) parts.push(convertThreeDigits(crore) + " Crore");
  if (lakh > 0) parts.push(convertThreeDigits(lakh) + " Lakh");
  if (thousand > 0) parts.push(convertThreeDigits(thousand) + " Thousand");
  if (hundred > 0) parts.push(convertThreeDigits(hundred));

  let words = parts.join(" ").trim();
  if (!words) words = "Zero";
  words += " Rupees";

  if (decimalPart > 0) {
    words += " and " + convertTwoDigits(decimalPart) + " Paise";
  } else {
    words += " and Zero Paise";
  }
  words += " Only";

  return words;
};

/**
 * Infer HSN Code from item details
 */
const getItemHsn = (item) => {
  if (item.hsn_code || item.hsn) return String(item.hsn_code || item.hsn);
  const name = (item.product_name || item.name || "").toLowerCase();
  if (name.includes("milk") || name.includes("paneer") || name.includes("curd") || name.includes("dahi")) return "0401";
  if (name.includes("ghee") || name.includes("butter")) return "0405";
  if (name.includes("banana") || name.includes("apple") || name.includes("mango") || name.includes("fruit")) return "0803";
  if (name.includes("tomato")) return "0702";
  if (name.includes("potato")) return "0701";
  if (name.includes("onion") || name.includes("veggie") || name.includes("vegetable") || name.includes("chilli")) return "0703";
  if (name.includes("dal") || name.includes("toor") || name.includes("moong") || name.includes("pulse")) return "0713";
  if (name.includes("rice") || name.includes("atta") || name.includes("flour")) return "1006";
  if (name.includes("oil")) return "1512";
  return "0401";
};

/**
 * Infer Unit from item details
 */
const getItemUnit = (item, qty) => {
  // API may return unit under several field names — check all of them first
  const rawUnit = item.unit_name || item.unit || item.unit_label || item.unit_type || "";
  if (rawUnit) return rawUnit;

  // Fall back: infer from product name + variant text
  const name    = (item.product_name || item.name || "").toLowerCase();
  const variant = (item.variant_name || item.variant || "").toLowerCase().trim();
  const combined = `${name} ${variant}`;

  if (combined.includes("ghee") || combined.includes("jar"))   return "Jar";
  if (combined.includes("milk") || combined.includes("pouch")) return "Pouch";
  if (combined.includes("bottle"))                              return "Bottle";
  if (combined.includes("pack")  || combined.includes("pkt"))  return "Pack";
  if (combined.includes("ltr")   || combined.includes("litre") || combined.includes("liter")) return "Ltr";
  if (combined.includes("ml"))                                  return "ml";

  // Weight-based: match "kg", "gm", "gram", "g" as standalone token in combined text
  if (/\b(kg|kilo|kilogram|gm|gram|g)\b/i.test(combined))      return "kg";

  // If variant is a pure number (e.g. "1", "0.5", "2") → it represents weight quantity (kg)
  if (variant && /^\d+(\.\d+)?$/.test(variant))                 return "kg";

  // HSN-based: vegetables (07xx) and fruits (08xx) are always sold by weight
  const hsn = String(item.hsn_code || item.hsn || "");
  if (/^07/.test(hsn) || /^08/.test(hsn))                       return "kg";

  // Fractional quantity means it's a weight-sold item
  if (!Number.isInteger(Number(qty))) return "kg";

  return "Pcs";
};


/**
 * Format quantity: 3 decimals for kg or fractional, integer otherwise
 */
const formatQuantity = (qty, unit) => {
  const n = parseFloat(qty ?? 1);
  if (String(unit).toLowerCase() === "kg" || !Number.isInteger(n)) {
    return n.toFixed(3);
  }
  return String(n);
};

/**
 * Infer GST Rate
 */
const getItemGstRate = (item) => {
  if (item.gst_rate !== undefined && item.gst_rate !== null) return parseFloat(item.gst_rate);
  if (item.gst_percent !== undefined && item.gst_percent !== null) return parseFloat(item.gst_percent);
  if (item.tax_rate !== undefined && item.tax_rate !== null) return parseFloat(item.tax_rate);
  const name = (item.product_name || item.name || "").toLowerCase();
  if (name.includes("ghee") || name.includes("cheese")) return 12;
  return 5;
};

/**
 * Builds the HTML content for an individual order invoice matching official layout
 */
export const buildSingleInvoiceHtml = (order) => {
  if (!order) return "";

  const orderId = order.order_id ?? order.id ?? "00000";
  const rawOrderNum = String(order.order_number ?? orderId).replace(/^#/, "");

  // Format Order No & Invoice No matching official template: KDF/INV/25-26/01854 & KDF/ORD/25-26/09271
  let formattedOrderNo = order.order_number;
  if (!formattedOrderNo || !formattedOrderNo.includes("/")) {
    const cleanNum = rawOrderNum.replace(/[^0-9]/g, "").slice(-5) || String(orderId).padStart(5, "0");
    formattedOrderNo = `KDF/ORD/25-26/${cleanNum.padStart(5, "0")}`;
  }

  let formattedInvoiceNo = order.invoice_number || order.invoice_no;
  if (!formattedInvoiceNo) {
    const cleanNum = rawOrderNum.replace(/[^0-9]/g, "").slice(-5) || String(orderId).padStart(5, "0");
    formattedInvoiceNo = `KDF/INV/25-26/${cleanNum.padStart(5, "0")}`;
  }

  const orderPlacedAt = order.placed_at ?? order.created_at ?? order.order_date;
  const orderInvoiceDate = order.invoice_date ?? order.delivered_at ?? orderPlacedAt;

  const rawPaymentMethod = (order.payment_method ?? order.payment_mode ?? "UPI").toUpperCase();
  const paymentMethod = rawPaymentMethod.includes("ONLINE") || rawPaymentMethod.includes("CASHFREE")
    ? "UPI"
    : (rawPaymentMethod === "COD" ? "Cash On Delivery" : rawPaymentMethod);

  const rawPaymentStatus = (order.payment_status ?? "PAID").toUpperCase();
  const paymentStatus = rawPaymentStatus === "PAID" ? "Paid" : (rawPaymentStatus === "UNPAID" ? "Unpaid" : rawPaymentStatus);

  const deliveryType = (order.delivery_type ?? "EXPRESS").toUpperCase();
  const deliveryOption = deliveryType === "EXPRESS"
    ? "Express Delivery (Within 30 Min)"
    : (deliveryType === "SCHEDULED" ? "Scheduled Delivery" : "Standard Delivery");

  const deliveryTime = order.delivery_slot || order.delivery_time || order.time_slot || order.delivery_slot_name || "";

  // Customer & Address Parsing
  const rawCustomerName =
    order.delivery_name ||
    order.delivery_address?.name ||
    order.address?.name ||
    order.customer_name ||
    "";

  const customerName = rawCustomerName
    ? (/^(Mr\.|Ms\.|Mrs\.|Dr\.)/i.test(rawCustomerName.trim())
        ? rawCustomerName.trim()
        : `Mr. ${rawCustomerName.trim()}`)
    : "";

  const deliveryAddressObj = typeof order.delivery_address === "object" ? order.delivery_address : null;
  const addressObj = typeof order.address === "object" ? order.address : null;

  const flat = deliveryAddressObj?.flat || deliveryAddressObj?.flat_no || addressObj?.flat || "";
  const area = deliveryAddressObj?.area || deliveryAddressObj?.street || addressObj?.area || "";
  const rawAddr = typeof order.delivery_address === "string" ? order.delivery_address : (order.address?.address || "");

  const city = order.delivery_city || deliveryAddressObj?.city || addressObj?.city || "Pune";
  const pincode = order.delivery_pincode || deliveryAddressObj?.pincode || addressObj?.pincode || "411045";
  const state = order.delivery_state || deliveryAddressObj?.state || addressObj?.state || "Maharashtra";

  const deliveryMobile =
    order.delivery_mobile ||
    deliveryAddressObj?.mobile ||
    addressObj?.mobile ||
    order.customer_phone ||
    "";

  const customerEmail =
    order.customer_email ||
    order.email ||
    order.user?.email ||
    "";

  const landmark =
    order.landmark ||
    deliveryAddressObj?.landmark ||
    addressObj?.landmark ||
    "";

  // Build address lines
  let billToLines = [];
  if (flat || area) {
    if (flat) billToLines.push(flat);
    if (area) billToLines.push(area);
  } else if (rawAddr) {
    const parts = rawAddr.split(",").map(p => p.trim()).filter(Boolean);
    if (parts.length > 2) {
      billToLines.push(parts.slice(0, 2).join(", "));
      billToLines.push(parts.slice(2, 4).join(", "));
    } else {
      billToLines.push(rawAddr);
    }
  } else {
    // no address at all — leave lines empty
  }
  if (city || pincode || state) {
    billToLines.push(`${[city, pincode].filter(Boolean).join(" - ")}${state ? `, ${state}, India` : ", India"}`);
  }

  const deliveryLines = [...billToLines];

  // Items processing
  const items = order.items || order.order_items || [];
  let totalTaxable = 0;
  let totalGst = 0;

  const tableRowsHtml = items.length > 0 ? items.map((item, idx) => {
    const name = item.product_name || item.name || "Product";
    const variant = (item.variant_name || item.variant || "").trim();
    const qty = parseFloat(item.quantity ?? item.qty ?? 1);
    const unit = getItemUnit(item, qty);
    const formattedQty = formatQuantity(qty, unit);
    const hsn = getItemHsn(item);
    const gstRate = getItemGstRate(item);

    let unitPrice = parseFloat(item.unit_price ?? item.price ?? 0);
    const lineTotal = parseFloat(item.line_total ?? item.total ?? 0);
    if (unitPrice === 0 && lineTotal > 0 && qty > 0) {
      unitPrice = lineTotal / qty;
    }
    if (unitPrice === 0) {
      unitPrice = 30.00;
    }

    const taxable = parseFloat((unitPrice * qty).toFixed(2));
    const gstAmt = parseFloat((taxable * (gstRate / 100)).toFixed(2));
    const rowTotal = parseFloat((taxable + gstAmt).toFixed(2));

    totalTaxable += taxable;
    totalGst += gstAmt;

    const displayName = variant ? `${name} (${variant})` : name;

    return `
      <tr>
        <td class="center">${idx + 1}</td>
        <td class="left">${displayName}</td>
        <td class="center">${hsn}</td>
        <td class="center">${formattedQty}</td>
        <td class="center">${unit}</td>
        <td class="right">${formatNumber(unitPrice)}</td>
        <td class="right">${formatNumber(taxable)}</td>
        <td class="center">${gstRate}%</td>
        <td class="right">${formatNumber(gstAmt)}</td>
        <td class="right">${formatNumber(rowTotal)}</td>
      </tr>
    `;
  }).join("") : `
    <tr>
      <td colspan="10" style="padding: 16px; text-align: center; color: #6b7280; font-size: 11px;">No items listed in order</td>
    </tr>
  `;

  // ── Totals: prefer API-provided values, fall back to item-level computation ──
  // Delivery charge may come under several field names depending on the API version
  const deliveryCharge = parseFloat(
    order.delivery_fee       ??
    order.delivery_charge    ??
    order.deliveryFee        ??
    order.delivery_cost      ??
    order.shipping_fee       ??
    order.shipping_charge    ??
    0
  );
  const deliveryChargeLabel = deliveryType === "EXPRESS" ? "Express" : (deliveryType === "SCHEDULED" ? "Scheduled" : "Standard");

  // Use API subtotal if available, otherwise sum of taxable amounts from items
  const apiSubtotal  = order.subtotal   != null ? parseFloat(order.subtotal)   : null;
  const apiTaxAmount = order.tax_amount != null ? parseFloat(order.tax_amount) : null;
  const apiTotal     = order.total_amount != null ? parseFloat(order.total_amount)
                     : order.total       != null ? parseFloat(order.total)
                     : null;

  const displaySubtotal = apiSubtotal  ?? totalTaxable;
  const displayTax      = apiTaxAmount ?? totalGst;
  // Final bill: prefer API total, else items subtotal + tax + delivery
  const finalBill = apiTotal ?? (displaySubtotal + displayTax + deliveryCharge);

  const amountInWords = amountToWordsIndian(finalBill);
  const totalItemsCount = items.length;

  return `
    <div class="invoice-wrapper">

      <!-- TOP HEADER -->
      <table class="inv-header-table">
        <tr>
          <!-- LEFT: LOGO + COMPANY DETAILS -->
          <td class="inv-header-left">
            <table style="border-collapse:collapse;width:100%;">
              <tr class="inv-logo-row">
                <td class="inv-logo-cell">
                  <img src="${INVOICE_LOGO_BASE64}" alt="Kisan Daily Fresh" class="inv-logo" />
                </td>
                <td class="inv-logo-text" style="vertical-align:top;">
                  <div class="inv-brand-name">KISAN DAILY FRESH</div>
                  <div class="inv-tagline">Farm Fresh. Delivered to Your Doorstep.</div>
                  <div class="inv-company-name">KKISANFRESH DAILY PRIVATE LIMITED</div>
                  <div class="inv-company-addr">CTS No 1108/7, Office No 5, 6 Ground Floor,<br>Business Square Unnati Housing Soc, Model Colony,<br>Shivajinagar Pune 411016, Maharashtra, India</div>
                  <div class="inv-company-addr">Phone: 9699664866</div>
                  <div class="inv-company-addr">customercare@kisandailyfresh.in | www.kisandailyfresh.in</div>
                </td>
              </tr>
            </table>
            <!-- GSTIN & FSSAI BOX -->
            <div class="inv-gstin-box">
              <span>GSTIN: 27AAMCK3978L1ZO</span>
              <span>FSSAI Lic. No.: 11526084000116</span>
            </div>
          </td>

          <!-- RIGHT: SALES INVOICE BADGE & ORDER DETAILS -->
          <td class="inv-header-right">
            <div><span class="inv-badge">SALES INVOICE</span></div>
            <table class="inv-details-table">
              <tr><td class="inv-details-label">Invoice No. :</td><td>${formattedInvoiceNo}</td></tr>
              <tr><td class="inv-details-label">Order No. :</td><td>${formattedOrderNo}</td></tr>
              <tr><td class="inv-details-label">Order Date :</td><td>${formatInvoiceDate(orderPlacedAt)}</td></tr>
              <tr><td class="inv-details-label">Invoice Date :</td><td>${formatInvoiceDate(orderInvoiceDate)}</td></tr>
              <tr><td class="inv-details-label">Payment Mode :</td><td>${paymentMethod}</td></tr>
              <tr><td class="inv-details-label">Payment Status :</td><td>${paymentStatus}</td></tr>
              <tr><td class="inv-details-label">Delivery Option :</td><td>${deliveryOption}</td></tr>
              ${deliveryTime ? `<tr><td class="inv-details-label">Delivery Time :</td><td>${deliveryTime}</td></tr>` : ""}
            </table>
          </td>
        </tr>
      </table>

      <!-- GREEN HORIZONTAL LINE -->
      <div class="inv-divider"></div>

      <!-- BILL TO & DELIVERY ADDRESS -->
      <table class="inv-addr-table">
        <tr>
          <td class="inv-addr-left">
            <div class="inv-addr-title">BILL TO</div>
            <div class="inv-addr-body">
              <div class="inv-addr-name">${customerName}</div>
              ${billToLines.map(l => `<div>${l}</div>`).join("")}
              ${deliveryMobile ? `<div>Mobile: ${deliveryMobile}</div>` : ""}
              ${customerEmail ? `<div>Email: ${customerEmail}</div>` : ""}
            </div>
          </td>
          <td class="inv-addr-right">
            <div class="inv-addr-title">DELIVERY ADDRESS</div>
            <div class="inv-addr-body">
              <div class="inv-addr-name">${customerName}</div>
              ${deliveryLines.map(l => `<div>${l}</div>`).join("")}
              ${deliveryMobile ? `<div>Mobile: ${deliveryMobile}</div>` : ""}
              ${landmark ? `<div>Landmark: ${landmark}</div>` : ""}
            </div>
          </td>
        </tr>
      </table>

      <!-- ITEMS TABLE -->
      <div class="inv-items-scroll">
        <table class="inv-items-table">
          <thead>
            <tr>
              <th class="center" style="width:30px;">Sr.</th>
              <th class="left">Item Name</th>
              <th class="center" style="width:60px;">HSN/SAC</th>
              <th class="center" style="width:46px;">Qty</th>
              <th class="center" style="width:46px;">Unit</th>
              <th class="right" style="width:68px;">Unit Price</th>
              <th class="right" style="width:68px;">Taxable</th>
              <th class="center" style="width:46px;">GST%</th>
              <th class="right" style="width:62px;">GST Amt</th>
              <th class="right" style="width:68px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>
      </div>

      <!-- BOTTOM SUMMARY -->
      <table class="inv-summary-table">
        <tr>
          <td class="inv-summary-left">
            <div class="inv-total-items">Total Items: ${totalItemsCount}</div>
            <div class="inv-words-label">AMOUNT IN WORDS</div>
            <div class="inv-words-text">${amountInWords}</div>
          </td>
          <td class="inv-summary-right">
            <table class="inv-totals-table">
              <tr><td>Sub Total</td><td class="right">&#8377; ${formatNumber(displaySubtotal)}</td></tr>
              <tr><td>Delivery Charge (${deliveryChargeLabel})</td><td class="right">&#8377; ${formatNumber(deliveryCharge)}</td></tr>
              <tr><td>Taxable Amount</td><td class="right">&#8377; ${formatNumber(displaySubtotal)}</td></tr>
              <tr><td>Total GST</td><td class="right">&#8377; ${formatNumber(displayTax)}</td></tr>
            </table>
            <div class="inv-final-bill">
              <span class="inv-final-label">FINAL BILL</span>
              <span class="inv-final-amount">&#8377; ${formatNumber(finalBill)}</span>
            </div>
          </td>
        </tr>
      </table>

    </div>
  `;
};

/**
 * Creates the complete standalone HTML page ready for printing / downloading
 */
export const createInvoiceDocument = (contentHtml, title = "Tax Invoice - Kisan Daily Fresh") => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title}</title>
  <style>
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      background-color: #f3f4f6;
      padding: 20px 10px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @page {
      size: A4;
      margin: 8mm;
    }
    @media print {
      body {
        background-color: #ffffff;
        padding: 0;
      }
      .no-print {
        display: none !important;
      }
      .invoice-wrapper {
        box-shadow: none !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
      }
      .inv-items-scroll { overflow: visible !important; }
    }

    /* ── Action Bar ── */
    .action-bar {
      max-width: 820px;
      margin: 0 auto 12px auto;
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }
    .btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 9px 20px;
      font-size: 13px;
      font-weight: 700;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      background-color: #006837;
      color: #ffffff;
      text-decoration: none;
      box-shadow: 0 2px 6px rgba(0,104,55,0.25);
      transition: background-color 0.2s ease, box-shadow 0.2s ease;
    }
    .btn:hover { background-color: #00502a; box-shadow: 0 4px 10px rgba(0,104,55,0.3); }

    /* ── Invoice Wrapper ── */
    .invoice-wrapper {
      max-width: 820px;
      margin: 0 auto 24px auto;
      background: #ffffff;
      padding: 28px 32px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      color: #111827;
      box-sizing: border-box;
      box-shadow: 0 2px 6px rgba(0,0,0,0.08);
      border-radius: 8px;
      page-break-after: always;
      break-after: page;
    }

    /* ── Header: logo+company LEFT, invoice details RIGHT ── */
    .inv-header-table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
    .inv-header-left  { vertical-align: top; width: 58%; padding-right: 12px; }
    .inv-header-right { vertical-align: top; width: 42%; text-align: right; }
    .inv-logo-cell    { vertical-align: top; width: 88px; padding-right: 14px; }
    .inv-logo         { width: 82px; height: 82px; object-fit: contain; border-radius: 8px; display: block; }
    .inv-brand-name   { font-size: 23px; font-weight: 900; color: #006837; letter-spacing: -0.3px; line-height: 1.15; }
    .inv-tagline      { font-size: 11.5px; color: #78350f; font-weight: 600; margin-top: 3px; }
    .inv-company-name { font-size: 12.5px; font-weight: 800; color: #111827; margin-top: 6px; }
    .inv-company-addr { font-size: 10.5px; color: #374151; line-height: 1.4; margin-top: 2px; }
    .inv-gstin-box    {
      margin-top: 10px;
      border: 1.5px solid #006837;
      border-radius: 6px;
      padding: 4px 12px;
      display: inline-flex;
      flex-wrap: wrap;
      gap: 4px 20px;
      font-size: 10.5px;
      font-weight: 700;
      color: #111827;
    }
    .inv-badge {
      background: #006837;
      color: #fff;
      padding: 7px 26px;
      border-radius: 6px;
      font-size: 15px;
      font-weight: 800;
      letter-spacing: 0.5px;
      display: inline-block;
      text-transform: uppercase;
      margin-bottom: 12px;
    }
    .inv-details-table { width: 100%; font-size: 11px; border-collapse: collapse; text-align: left; }
    .inv-details-table td { padding: 2px 0; color: #111827; font-weight: 500; }
    .inv-details-label { width: 44%; }

    /* ── Divider ── */
    .inv-divider { border-bottom: 2px solid #006837; margin: 12px 0 16px 0; }

    /* ── Bill To / Delivery ── */
    .inv-addr-table { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
    .inv-addr-left  { width: 50%; vertical-align: top; padding-right: 15px; }
    .inv-addr-right { width: 50%; vertical-align: top; border-left: 1.5px solid #006837; padding-left: 20px; }
    .inv-addr-title { font-size: 13px; font-weight: 800; color: #006837; margin-bottom: 6px; letter-spacing: 0.3px; }
    .inv-addr-body  { font-size: 11px; color: #111827; line-height: 1.45; }
    .inv-addr-name  { font-weight: 600; }

    /* ── Items Table ── */
    .inv-items-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 20px; }
    .inv-items-table  { width: 100%; border-collapse: collapse; min-width: 620px; }
    .inv-items-table thead tr { background: #006837; color: #ffffff; }
    .inv-items-table th {
      padding: 8px 4px;
      font-size: 11px;
      font-weight: 700;
    }
    .inv-items-table th.left  { text-align: left;   padding-left: 8px; }
    .inv-items-table th.right { text-align: right;  padding-right: 8px; }
    .inv-items-table th.center{ text-align: center; }
    .inv-items-table td {
      padding: 7px 4px;
      font-size: 11px;
      color: #111827;
      border-bottom: 1px solid #f3f4f6;
    }
    .inv-items-table td.left  { text-align: left;   font-weight: 500; padding-left: 8px; }
    .inv-items-table td.right { text-align: right;  padding-right: 8px; }
    .inv-items-table td.center{ text-align: center; }

    /* ── Bottom Summary ── */
    .inv-summary-table { width: 100%; border-collapse: collapse; }
    .inv-summary-left  { width: 52%; vertical-align: top; padding-right: 20px; }
    .inv-summary-right { width: 48%; vertical-align: top; }
    .inv-total-items   { font-size: 12.5px; font-weight: 700; color: #111827; margin-bottom: 24px; }
    .inv-words-label   { font-size: 11.5px; font-weight: 800; color: #006837; margin-bottom: 5px; text-transform: uppercase; }
    .inv-words-text    { font-size: 11.5px; font-weight: 500; color: #111827; line-height: 1.45; }
    .inv-totals-table  { width: 100%; font-size: 11.5px; border-collapse: collapse; }
    .inv-totals-table td { padding: 4px 0; color: #111827; }
    .inv-totals-table td.right { text-align: right; font-weight: 600; }
    .inv-totals-table td.discount { color: #15803d; }
    .inv-final-bill {
      background: #006837;
      color: #fff;
      border-radius: 8px;
      padding: 8px 16px;
      margin-top: 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .inv-final-label  { font-size: 14px; font-weight: 800; letter-spacing: 0.5px; }
    .inv-final-amount { font-size: 15px; font-weight: 800; }

    /* ── Footer ── */
    .inv-footer {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px dashed #d1d5db;
      text-align: center;
      color: #6b7280;
      font-size: 10px;
    }
    .inv-footer-msg { font-weight: 600; color: #006837; font-size: 11px; margin-bottom: 2px; }

    /* ══════════════════════════════════════
       MOBILE  (≤ 600px)
       ══════════════════════════════════════ */
    @media (max-width: 600px) {
      body { padding: 10px 6px; }

      /* Action bar — full width stacked */
      .action-bar {
        flex-direction: column;
        align-items: stretch;
        margin-bottom: 10px;
      }
      .btn {
        width: 100%;
        justify-content: center;
        padding: 11px 16px;
        font-size: 14px;
        border-radius: 10px;
      }

      /* Invoice wrapper — tight padding */
      .invoice-wrapper { padding: 14px 12px; border-radius: 6px; }

      /* ── Header: stack LEFT block, then RIGHT block ── */
      .inv-header-table,
      .inv-header-table tbody,
      .inv-header-table tr { display: block; width: 100%; }
      .inv-header-left  { display: block; width: 100%; padding-right: 0; margin-bottom: 14px; }
      .inv-header-right { display: block; width: 100%; text-align: left; }

      /* Logo row inside left block */
      .inv-logo-row,
      .inv-logo-inner { display: block; width: 100%; }
      .inv-logo-cell  { display: inline-block; width: auto; padding-right: 12px; vertical-align: top; }
      .inv-logo-text  { display: inline-block; vertical-align: top; max-width: calc(100% - 100px); }
      .inv-logo       { width: 64px; height: 64px; }
      .inv-brand-name { font-size: 18px; }
      .inv-tagline    { font-size: 10px; }
      .inv-company-name { font-size: 11px; }
      .inv-company-addr { font-size: 9.5px; }

      /* GSTIN / FSSAI wrap on mobile */
      .inv-gstin-box { display: flex; flex-direction: column; gap: 3px; padding: 6px 10px; }

      /* Badge + details table */
      .inv-badge { font-size: 13px; padding: 6px 18px; margin-bottom: 10px; }
      .inv-details-table { font-size: 10.5px; }
      .inv-details-label { width: 48%; }

      /* ── Bill To / Delivery: stack vertically ── */
      .inv-addr-table,
      .inv-addr-table tbody,
      .inv-addr-table tr { display: block; width: 100%; }
      .inv-addr-left  { display: block; width: 100%; padding-right: 0; margin-bottom: 14px; }
      .inv-addr-right {
        display: block;
        width: 100%;
        border-left: none;
        border-top: 1.5px solid #006837;
        padding-left: 0;
        padding-top: 12px;
      }
      .inv-addr-title { font-size: 12px; }
      .inv-addr-body  { font-size: 10.5px; }

      /* ── Items table: horizontal scroll, smaller font ── */
      .inv-items-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 4px; }
      .inv-items-table  { min-width: 560px; }
      .inv-items-table th,
      .inv-items-table td { font-size: 9.5px; padding: 6px 3px; }
      .inv-items-table th.left,
      .inv-items-table td.left { padding-left: 6px; }
      .inv-items-table th.right,
      .inv-items-table td.right { padding-right: 6px; }

      /* ── Bottom summary: stack left above right ── */
      .inv-summary-table,
      .inv-summary-table tbody,
      .inv-summary-table tr { display: block; width: 100%; }
      .inv-summary-left  { display: block; width: 100%; padding-right: 0; margin-bottom: 16px; }
      .inv-summary-right { display: block; width: 100%; }
      .inv-total-items   { margin-bottom: 10px; }
      .inv-totals-table  { font-size: 11px; }
      .inv-final-bill    { border-radius: 8px; padding: 10px 14px; }
      .inv-final-label   { font-size: 13px; }
      .inv-final-amount  { font-size: 14px; }
    }
  </style>
</head>
<body>
  <div class="action-bar no-print">
    <button class="btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
  </div>
  ${contentHtml}
</body>
</html>`;
};

/**
 * Fetches dynamic order invoice data directly from API
 * Endpoint: /api/v1/customer/orders/:order_id/invoice
 */
export const fetchOrderInvoice = async (orderId) => {
  if (!orderId) {
    throw new Error("Order ID is required to fetch invoice.");
  }
  const cleanId = String(orderId).replace(/^#/, "");
  return await apiFetch(`/api/v1/customer/orders/${cleanId}/invoice`);
};

/**
 * Builds HTML for dynamic invoice returned directly by the API endpoint:
 * /api/v1/customer/orders/:order_id/invoice
 */
export const buildDynamicInvoiceHtml = (invoiceData) => {
  if (!invoiceData) return "";

  const payload = invoiceData?.data || invoiceData;
  const company = payload?.company_info || {};
  const invoiceInfo = payload?.invoice_info || {};
  const billTo = payload?.bill_to || {};
  const deliveryAddress = payload?.delivery_address || {};
  const items = payload?.items || [];
  const summary = payload?.summary || {};
  const footer = payload?.footer || {};

  // Company details from API
  const companyName = company.company_name || "KKISANFRESH DAILY PRIVATE LIMITED";
  const brandName = company.brand_name || "KISAN DAILY FRESH";
  const tagline = company.tagline || "Farm Fresh. Delivered to Your Doorstep.";
  const logoUrl = company.logo_url || INVOICE_LOGO_BASE64;
  const companyAddress = company.address || "CTS No 1108/7, Office No 5, 6 Ground Floor, Business Square Unnati Housing Soc, Model Colony, Shivajinagar Pune 411016, Maharashtra, India";
  const companyPhone = company.phone || "9699664866";
  const companyEmail = company.email || "customercare@kisandailyfresh.in";
  const companyWebsite = company.website || "www.kisandailyfresh.in";
  const gstin = company.gstin || "27AAMCK3978L1ZO";
  const fssai = company.fssai_lic_no || "11526084000116";

  // Invoice & Order details from API
  const invoiceTitle = invoiceInfo.invoice_title || "SALES INVOICE";
  const invoiceNo = invoiceInfo.invoice_no || `KDF/INV/26-27/${String(invoiceInfo.order_id || "").padStart(5, "0")}`;
  const orderNo = invoiceInfo.order_no || `ORD-${invoiceInfo.order_id || ""}`;
  const orderDate = invoiceInfo.order_date || "";
  const invoiceDate = invoiceInfo.invoice_date || orderDate || "";
  const paymentMode = (invoiceInfo.payment_mode || "UPI").toUpperCase();
  const paymentStatus = invoiceInfo.payment_status || "Pending";
  const deliveryOption = invoiceInfo.delivery_option || "Standard Delivery";
  const deliveryTime = invoiceInfo.delivery_time || "";

  // Bill To details from API
  const rawBillToName = billTo.name ? billTo.name.trim() : "";
  const billToName = rawBillToName
    ? (/^(Mr\.|Ms\.|Mrs\.|Dr\.)/i.test(rawBillToName) ? rawBillToName : `Mr. ${rawBillToName}`)
    : "Valued Customer";

  let billAddressLines = [];
  if (billTo.address) {
    billAddressLines.push(billTo.address);
    const cityPin = [billTo.city, billTo.pincode].filter(Boolean).join(" - ");
    const stateCountry = [billTo.state, billTo.country || "India"].filter(Boolean).join(", ");
    const line2 = [cityPin, stateCountry].filter(Boolean).join(", ");
    if (line2) billAddressLines.push(line2);
  } else if (billTo.full_address) {
    billAddressLines.push(billTo.full_address);
  }

  const billToMobile = billTo.mobile || "";
  const billToEmail = billTo.email || "";

  // Delivery Address details from API
  const rawDeliveryName = deliveryAddress.name ? deliveryAddress.name.trim() : rawBillToName;
  const deliveryName = rawDeliveryName
    ? (/^(Mr\.|Ms\.|Mrs\.|Dr\.)/i.test(rawDeliveryName) ? rawDeliveryName : `Mr. ${rawDeliveryName}`)
    : billToName;

  let deliveryAddressLines = [];
  if (deliveryAddress.address) {
    deliveryAddressLines.push(deliveryAddress.address);
    const cityPin = [deliveryAddress.city, deliveryAddress.pincode].filter(Boolean).join(" - ");
    const stateCountry = [deliveryAddress.state, deliveryAddress.country || "India"].filter(Boolean).join(", ");
    const line2 = [cityPin, stateCountry].filter(Boolean).join(", ");
    if (line2) deliveryAddressLines.push(line2);
  } else if (deliveryAddress.full_address) {
    deliveryAddressLines.push(deliveryAddress.full_address);
  } else {
    deliveryAddressLines = [...billAddressLines];
  }

  const deliveryMobile = deliveryAddress.mobile || billToMobile;
  const landmark = deliveryAddress.landmark || "";

  // Items table rows from API
  const tableRowsHtml = items.length > 0 ? items.map((item, idx) => {
    const srNo = item.sr_no ?? (idx + 1);
    const name = item.item_name || item.product_name || item.name || "Product";
    const variant = (item.variant_name || item.variant || "").trim();
    const displayName = variant && !name.toLowerCase().includes(variant.toLowerCase())
      ? `${name} (${variant})`
      : name;
    const hsn = item.hsn_sac || item.hsn_code || "0401";
    const qty = item.qty ?? item.quantity ?? 1;
    const unit = item.unit || "Pcs";
    const unitPrice = formatNumber(item.unit_price ?? item.price ?? 0);
    const taxable = formatNumber(item.taxable_amount ?? (Number(unitPrice) * Number(qty)));
    const gstPercentage = item.gst_percentage ?? (item.gst_rate != null ? `${item.gst_rate}%` : "0%");
    const gstAmt = formatNumber(item.gst_amount ?? 0);
    const total = formatNumber(item.total_amount ?? item.total ?? (Number(taxable) + Number(gstAmt)));

    return `
      <tr>
        <td class="center">${srNo}</td>
        <td class="left">${displayName}</td>
        <td class="center">${hsn}</td>
        <td class="center">${qty}</td>
        <td class="center">${unit}</td>
        <td class="right">${unitPrice}</td>
        <td class="right">${taxable}</td>
        <td class="center">${gstPercentage}</td>
        <td class="right">${gstAmt}</td>
        <td class="right">${total}</td>
      </tr>
    `;
  }).join("") : `
    <tr>
      <td colspan="10" style="padding: 16px; text-align: center; color: #6b7280; font-size: 11px;">No items listed in order</td>
    </tr>
  `;

  // Summary & Totals from API
  const totalItems = summary.total_items ?? items.length;
  const subTotal = formatNumber(summary.sub_total ?? 0);
  const deliveryCharge = formatNumber(summary.delivery_charge ?? 0);
  const rawDeliveryLabel = summary.delivery_charge_label || "Delivery Charge";
  const deliveryChargeLabel = rawDeliveryLabel.toLowerCase().includes("delivery") || rawDeliveryLabel.toLowerCase().includes("charge")
    ? rawDeliveryLabel
    : `Delivery Charge (${rawDeliveryLabel})`;
  const discountAmount = parseFloat(summary.discount_amount ?? 0);
  const taxableAmount = formatNumber(summary.taxable_amount ?? summary.sub_total ?? 0);
  const totalGst = formatNumber(summary.total_gst ?? 0);
  const finalBill = formatNumber(summary.final_bill ?? summary.grand_total ?? 0);
  const amountInWords = summary.amount_in_words || amountToWordsIndian(finalBill);

  // Footer messages from API
  const footerMessage = footer.message || "Thank you for choosing Kisan Daily Fresh! ♥";
  const footerTerms = footer.terms || "This is a computer generated invoice and does not require a physical signature.";

  return `
    <div class="invoice-wrapper">

      <!-- TOP HEADER -->
      <table class="inv-header-table">
        <tr>
          <!-- LEFT: LOGO + COMPANY DETAILS -->
          <td class="inv-header-left">
            <table style="border-collapse:collapse;width:100%;">
              <tr class="inv-logo-row">
                <td class="inv-logo-cell">
                  <img src="${logoUrl}" alt="${brandName}" onerror="this.onerror=null;this.src='${INVOICE_LOGO_BASE64}'" class="inv-logo" />
                </td>
                <td class="inv-logo-text" style="vertical-align:top;">
                  <div class="inv-brand-name">${brandName}</div>
                  <div class="inv-tagline">${tagline}</div>
                  <div class="inv-company-name">${companyName}</div>
                  <div class="inv-company-addr">${companyAddress}</div>
                  <div class="inv-company-addr">Phone: ${companyPhone}</div>
                  <div class="inv-company-addr">${companyEmail} | ${companyWebsite}</div>
                </td>
              </tr>
            </table>
            <!-- GSTIN & FSSAI BOX -->
            <div class="inv-gstin-box">
              <span>GSTIN: ${gstin}</span>
              <span>FSSAI Lic. No.: ${fssai}</span>
            </div>
          </td>

          <!-- RIGHT: SALES INVOICE BADGE & ORDER DETAILS -->
          <td class="inv-header-right">
            <div><span class="inv-badge">${invoiceTitle}</span></div>
            <table class="inv-details-table">
              <tr><td class="inv-details-label">Invoice No. :</td><td>${invoiceNo}</td></tr>
              <tr><td class="inv-details-label">Order No. :</td><td>${orderNo}</td></tr>
              <tr><td class="inv-details-label">Order Date :</td><td>${orderDate}</td></tr>
              <tr><td class="inv-details-label">Invoice Date :</td><td>${invoiceDate}</td></tr>
              <tr><td class="inv-details-label">Payment Mode :</td><td>${paymentMode}</td></tr>
              <tr><td class="inv-details-label">Payment Status :</td><td>${paymentStatus}</td></tr>
              <tr><td class="inv-details-label">Delivery Option :</td><td>${deliveryOption}</td></tr>
              ${deliveryTime ? `<tr><td class="inv-details-label">Delivery Time :</td><td>${deliveryTime}</td></tr>` : ""}
            </table>
          </td>
        </tr>
      </table>

      <!-- GREEN HORIZONTAL LINE -->
      <div class="inv-divider"></div>

      <!-- BILL TO & DELIVERY ADDRESS -->
      <table class="inv-addr-table">
        <tr>
          <td class="inv-addr-left">
            <div class="inv-addr-title">BILL TO</div>
            <div class="inv-addr-body">
              <div class="inv-addr-name">${billToName}</div>
              ${billAddressLines.map(l => `<div>${l}</div>`).join("")}
              ${billToMobile ? `<div>Mobile: ${billToMobile}</div>` : ""}
              ${billToEmail ? `<div>Email: ${billToEmail}</div>` : ""}
            </div>
          </td>
          <td class="inv-addr-right">
            <div class="inv-addr-title">DELIVERY ADDRESS</div>
            <div class="inv-addr-body">
              <div class="inv-addr-name">${deliveryName}</div>
              ${deliveryAddressLines.map(l => `<div>${l}</div>`).join("")}
              ${deliveryMobile ? `<div>Mobile: ${deliveryMobile}</div>` : ""}
              ${landmark ? `<div>Landmark: ${landmark}</div>` : ""}
            </div>
          </td>
        </tr>
      </table>

      <!-- ITEMS TABLE -->
      <div class="inv-items-scroll">
        <table class="inv-items-table">
          <thead>
            <tr>
              <th class="center" style="width:30px;">Sr.</th>
              <th class="left">Item Name</th>
              <th class="center" style="width:60px;">HSN/SAC</th>
              <th class="center" style="width:46px;">Qty</th>
              <th class="center" style="width:46px;">Unit</th>
              <th class="right" style="width:68px;">Unit Price</th>
              <th class="right" style="width:68px;">Taxable</th>
              <th class="center" style="width:46px;">GST%</th>
              <th class="right" style="width:62px;">GST Amt</th>
              <th class="right" style="width:68px;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>
      </div>

      <!-- BOTTOM SUMMARY -->
      <table class="inv-summary-table">
        <tr>
          <td class="inv-summary-left">
            <div class="inv-total-items">Total Items: ${totalItems}</div>
            <div class="inv-words-label">AMOUNT IN WORDS</div>
            <div class="inv-words-text">${amountInWords}</div>
          </td>
          <td class="inv-summary-right">
            <table class="inv-totals-table">
              <tr><td>Sub Total</td><td class="right">&#8377; ${subTotal}</td></tr>
              <tr><td>Taxable Amount</td><td class="right">&#8377; ${taxableAmount}</td></tr>
              <tr><td>${deliveryChargeLabel}</td><td class="right">&#8377; ${deliveryCharge}</td></tr>
              ${discountAmount > 0 ? `<tr><td class="discount">Discount</td><td class="right discount">- &#8377; ${formatNumber(discountAmount)}</td></tr>` : ""}
              <tr><td>Total GST</td><td class="right">&#8377; ${totalGst}</td></tr>
            </table>
            <div class="inv-final-bill">
              <span class="inv-final-label">FINAL BILL</span>
              <span class="inv-final-amount">&#8377; ${finalBill}</span>
            </div>
          </td>
        </tr>
      </table>

      <!-- FOOTER -->
      <div class="inv-footer">
        <div class="inv-footer-msg">${footerMessage}</div>
        <div>${footerTerms}</div>
      </div>

    </div>
  `;
};

/**
 * Fetches dynamic invoice data from API and downloads / opens printable window.
 * Matches endpoint: /api/v1/customer/orders/:order_id/invoice
 * @param {number|string|object} orderIdOrOrder - order ID or order object containing order_id / id
 */
export const fetchAndDownloadInvoice = async (orderIdOrOrder) => {
  let orderId = null;
  if (typeof orderIdOrOrder === "object" && orderIdOrOrder !== null) {
    orderId = orderIdOrOrder.order_id ?? orderIdOrOrder.id;
  } else if (orderIdOrOrder) {
    orderId = orderIdOrOrder;
  }

  if (!orderId) {
    toast.error("Order ID not found. Unable to download invoice.");
    return false;
  }

  const cleanOrderId = String(orderId).replace(/^#/, "");
  const toastId = toast.loading("Fetching invoice from server...", { id: `invoice-download-${cleanOrderId}` });

  try {
    const res = await apiFetch(`/api/v1/customer/orders/${cleanOrderId}/invoice`);
    const invoiceData = res?.data;

    if (!invoiceData) {
      throw new Error(res?.message || "Invoice details not found for this order.");
    }

    const invoiceInfo = invoiceData.invoice_info || {};
    const orderNo = invoiceInfo.order_no || invoiceInfo.invoice_no || `ORD_${cleanOrderId}`;
    const fileTitle = `Invoice_${String(orderNo).replace(/[^a-zA-Z0-9_-]/g, "_")}`;

    const invoiceHtml = buildDynamicInvoiceHtml(invoiceData);
    const fullHtml = createInvoiceDocument(invoiceHtml, fileTitle);

    // 1. Direct file download as HTML file
    const blob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${fileTitle}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 2. Open printable preview window so user can also Save as PDF directly
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (e) {
          console.error("Auto print failed:", e);
        }
      }, 400);
    }

    setTimeout(() => URL.revokeObjectURL(downloadUrl), 10000);

    toast.success("Invoice downloaded successfully!", { id: toastId });
    return true;
  } catch (error) {
    console.error("Failed to fetch dynamic invoice:", error);
    toast.error(error.message || "Failed to download invoice. Please try again.", { id: toastId });
    return false;
  }
};

/**
 * Downloads a single order invoice dynamically from API.
 * Compatible drop-in replacement for previous downloadInvoice callers.
 */
export const downloadInvoice = (orderIdOrOrder) => {
  return fetchAndDownloadInvoice(orderIdOrOrder);
};

/**
 * Downloads invoices for all orders in the list
 */
export const downloadAllInvoices = async (orders = []) => {
  if (!orders || orders.length === 0) return;

  const toastId = toast.loading(`Fetching ${orders.length} invoices...`, { id: "bulk-invoice-download" });

  try {
    const htmlSnippets = [];

    for (const ord of orders) {
      const orderId = ord.order_id ?? ord.id;
      if (orderId) {
        try {
          const res = await apiFetch(`/api/v1/customer/orders/${orderId}/invoice`);
          if (res?.data) {
            htmlSnippets.push(buildDynamicInvoiceHtml(res.data));
            continue;
          }
        } catch (e) {
          console.warn(`Could not fetch dynamic invoice for order ${orderId}:`, e);
        }
      }
      // Fallback if individual API fetch failed
      htmlSnippets.push(buildSingleInvoiceHtml(ord));
    }

    const title = `All_Invoices_KisanDailyFresh_${new Date().toISOString().slice(0, 10)}`;
    const fullHtml = createInvoiceDocument(htmlSnippets.join(""), title);

    const blob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${title}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (e) {
          console.error("Auto print failed:", e);
        }
      }, 400);
    }

    setTimeout(() => URL.revokeObjectURL(downloadUrl), 10000);
    toast.success("Invoices downloaded successfully!", { id: toastId });
  } catch (error) {
    console.error("Bulk invoice download error:", error);
    toast.error("Failed to download all invoices.", { id: toastId });
  }
};
